import {
  Message,
  MessageContent,
  ApprovalState,
  FieldValueEntryPayloadModel,
  GetWorkItemFieldValuesPayloadModel,
  ValueType,
} from '@lichtblick/types';

import { formatName } from './formatName';

export const getMessages = (fields: GetWorkItemFieldValuesPayloadModel[]) => {
  const entries = fields.flatMap(({ fieldValue }) => {
    if (!fieldValue?.values) {
      return [];
    }

    return fieldValue.values.map((item) => {
      return {
        ...item,
        value: item.value ? JSON.parse(item.value) : item.value,
      };
    });
  });

  if (!entries) {
    return [];
  }

  return createMessages(entries);
};

export const createMessages = (entries: FieldValueEntryPayloadModel[]): Message[] => {
  const messages: Message[] = entries.flatMap((entry) => {
    const baseMessage: Message = {
      status: ApprovalState.Pending,
      content: mapContent(entry.type, entry.value, !!entry.isArray),
      comment: entry.comment,
      id: entry.id,
      type: entry.type,
      isArray: entry.isArray,
      itemDecisions: entry.itemDecisions ?? undefined,
      author: entry.lastSubmitBy
        ? {
            id: entry.lastSubmitBy?.id,
            isCraftsman: true,
            name: formatName({ firstName: entry.lastSubmitBy.firstName, lastName: entry.lastSubmitBy.lastName }),
            // team: entry.lastSubmitBy.teamName,
          }
        : undefined,
      date: entry.lastSubmitAtUtc,
    };

    let additionalMessage: Message | null = null;

    if (entry.approvalState === ApprovalState.ServerApproved || entry.approvalState === ApprovalState.Rejected) {
      additionalMessage = {
        ...baseMessage,
        id: entry.id + entry.decisionMadeAtUtc,
        content: { text: entry.rejectReason ?? '' },
        status: entry.approvalState,
        author: entry.decisionMadeBy
          ? {
              id: entry.decisionMadeBy.id,
              isCraftsman: false,
              name: formatName({ firstName: entry.decisionMadeBy.firstName, lastName: entry.decisionMadeBy.lastName }),
            }
          : undefined,
        date: entry.decisionMadeAtUtc,
      };
    }

    if (entry.approvalState === ApprovalState.Undoed || entry.approvalState === ApprovalState.ClientApproved) {
      additionalMessage = {
        ...baseMessage,
        id: entry.id + entry.decisionMadeAtUtc,
        content: mapContent(entry.type, entry.value, !!entry.isArray),
        status: entry.approvalState,
        author: entry.lastSubmitBy
          ? {
              id: entry.lastSubmitBy?.id,
              isCraftsman: true,
              name: formatName({ firstName: entry.lastSubmitBy.firstName, lastName: entry.lastSubmitBy.lastName }),
              // team: entry.lastSubmitBy.teamName,
            }
          : undefined,
        date: entry.lastSubmitAtUtc,
      };
    }

    return additionalMessage ? [baseMessage, additionalMessage] : [baseMessage];
  });

  messages.sort((a: Message, b: Message) => new Date(a.date).getTime() - new Date(b.date).getTime());

  return messages;
};

const mapContent = (type: ValueType, value: any, isArray: boolean): MessageContent => {
  if (type === ValueType.Boolean) {
    return { isYesNo: value };
  }

  if (type === ValueType.Signature) {
    return { signature: { hasAcceptedTerms: value.acceptedTerms, image: value.image } };
  }

  if (type === ValueType.Image) {
    if (isArray) {
      return { image: value };
    }

    return { image: [value] };
  }

  if (isArray) {
    return { multipleChoice: value };
  }

  return { text: value };
};
